import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, Grid, Badge, Icon, Stat, StatLabel, StatNumber, StatHelpText, StatArrow, Divider, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaDatabase, FaChartBar, FaMapMarkerAlt, FaArrowUp, FaCheckCircle, FaChartLine, FaCalculator, FaBrain } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyStrategyDataDriven: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/strategy/data-driven"
        fallbackTitle="Data-Driven Property Strategy"
        fallbackDescription="Investment strategies backed by comprehensive market data and analysis"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="linear-gradient(135deg, #0f172a 0%, #1e40af 100%)" color="white" py={20}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Badge colorScheme="blue" bg="blue.400" color="white" px={4} py={2} borderRadius="full" fontSize="sm">
                Investment Strategy
              </Badge>
              <Heading size="3xl" fontWeight="black" lineHeight="1.2">
                Invest With Confidence, Not Guesswork
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.900" lineHeight="1.8">
                You didn't build your career by making emotional decisions. Property investment should be the same—backed by data, research, and clear analysis so you know you're making the right moves.
              </Text>
            </VStack>
            <Box bg="whiteAlpha.200" p={8} borderRadius="2xl" backdropFilter="blur(10px)">
              <VStack spacing={4}>
                <Icon as={FaDatabase} boxSize={16} color="blue.300" />
                <Text fontSize="2xl" fontWeight="bold" textAlign="center">Data-Backed Analysis</Text>
                <Text fontSize="sm" textAlign="center" color="whiteAlpha.900">
                  Recommendations built on comprehensive market data, demographic trends, and infrastructure pipelines
                </Text>
              </VStack>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Key Metrics Cards */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">The Numbers That Matter</Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            We monitor the metrics that predict long-term growth—so you can be confident your investment decisions are based on facts, not speculation
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Box bg="blue.50" p={6} borderRadius="xl" borderLeft="4px solid" borderColor="blue.500">
            <Stat>
              <StatLabel fontSize="sm" color="gray.600" mb={2}>Capital Growth Rate</StatLabel>
              <StatNumber fontSize="3xl" color="blue.600">8.2%</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                National average
              </StatHelpText>
            </Stat>
          </Box>

          <Box bg="green.50" p={6} borderRadius="xl" borderLeft="4px solid" borderColor="green.500">
            <Stat>
              <StatLabel fontSize="sm" color="gray.600" mb={2}>Rental Yield</StatLabel>
              <StatNumber fontSize="3xl" color="green.600">4.1%</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Metropolitan avg
              </StatHelpText>
            </Stat>
          </Box>

          <Box bg="purple.50" p={6} borderRadius="xl" borderLeft="4px solid" borderColor="purple.500">
            <Stat>
              <StatLabel fontSize="sm" color="gray.600" mb={2}>Days on Market</StatLabel>
              <StatNumber fontSize="3xl" color="purple.600">32</StatNumber>
              <StatHelpText>
                <StatArrow type="decrease" />
                Strong demand
              </StatHelpText>
            </Stat>
          </Box>

          <Box bg="orange.50" p={6} borderRadius="xl" borderLeft="4px solid" borderColor="orange.500">
            <Stat>
              <StatLabel fontSize="sm" color="gray.600" mb={2}>Auction Clearance</StatLabel>
              <StatNumber fontSize="3xl" color="orange.600">73%</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Above benchmark
              </StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Data Analysis Approach */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={12}>
            <VStack align="stretch" spacing={6}>
              <Heading size="xl" color="gray.800">Our Analytical Framework</Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="1.8">
                We analyse multiple data sources to provide you with a complete picture of each investment opportunity.
              </Text>

              <List spacing={4} mt={4}>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={FaCheckCircle} color="blue.500" mt={1} boxSize={5} />
                  <Box>
                    <Text fontWeight="bold" color="gray.800">Historical Performance Analysis</Text>
                    <Text color="gray.600" fontSize="sm">Track 10+ years of suburb growth patterns</Text>
                  </Box>
                </ListItem>

                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={FaCheckCircle} color="blue.500" mt={1} boxSize={5} />
                  <Box>
                    <Text fontWeight="bold" color="gray.800">Infrastructure Pipeline Review</Text>
                    <Text color="gray.600" fontSize="sm">Identify areas with planned development</Text>
                  </Box>
                </ListItem>

                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={FaCheckCircle} color="blue.500" mt={1} boxSize={5} />
                  <Box>
                    <Text fontWeight="bold" color="gray.800">Demographic Trends</Text>
                    <Text color="gray.600" fontSize="sm">Population growth and migration patterns</Text>
                  </Box>
                </ListItem>

                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={FaCheckCircle} color="blue.500" mt={1} boxSize={5} />
                  <Box>
                    <Text fontWeight="bold" color="gray.800">Economic Indicators</Text>
                    <Text color="gray.600" fontSize="sm">Employment rates and wage growth analysis</Text>
                  </Box>
                </ListItem>

                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={FaCheckCircle} color="blue.500" mt={1} boxSize={5} />
                  <Box>
                    <Text fontWeight="bold" color="gray.800">Supply & Demand Dynamics</Text>
                    <Text color="gray.600" fontSize="sm">Vacancy rates and construction activity</Text>
                  </Box>
                </ListItem>
              </List>
            </VStack>

            <SimpleGrid columns={2} spacing={6}>
              <Box bg="white" p={6} borderRadius="xl" boxShadow="lg" textAlign="center">
                <Icon as={FaChartBar} boxSize={10} color="blue.600" mb={4} />
                <Heading size="md" mb={2} color="gray.800">Market Analysis</Heading>
                <Text fontSize="sm" color="gray.600">Comprehensive suburb comparisons</Text>
              </Box>

              <Box bg="white" p={6} borderRadius="xl" boxShadow="lg" textAlign="center">
                <Icon as={FaMapMarkerAlt} boxSize={10} color="green.600" mb={4} />
                <Heading size="md" mb={2} color="gray.800">Location Scoring</Heading>
                <Text fontSize="sm" color="gray.600">Proximity to amenities</Text>
              </Box>

              <Box bg="white" p={6} borderRadius="xl" boxShadow="lg" textAlign="center">
                <Icon as={FaArrowUp} boxSize={10} color="purple.600" mb={4} />
                <Heading size="md" mb={2} color="gray.800">Growth Forecasting</Heading>
                <Text fontSize="sm" color="gray.600">5-10 year projections</Text>
              </Box>

              <Box bg="white" p={6} borderRadius="xl" boxShadow="lg" textAlign="center">
                <Icon as={FaCalculator} boxSize={10} color="orange.600" mb={4} />
                <Heading size="md" mb={2} color="gray.800">ROI Modeling</Heading>
                <Text fontSize="sm" color="gray.600">Cash flow analysis</Text>
              </Box>
            </SimpleGrid>
          </Grid>
        </Container>
      </Box>

      {/* Market Insights Section */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">Current Market Insights</Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            Latest trends and opportunities in the Australian property market
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <Box bg="gradient.to-br" bgGradient="linear(to-br, blue.500, blue.700)" p={8} borderRadius="2xl" color="white" position="relative" overflow="hidden">
            <Badge colorScheme="blue" bg="whiteAlpha.300" color="white" mb={4} px={3} py={1}>
              Growth Hotspot
            </Badge>
            <Heading size="lg" mb={4}>Regional Migration</Heading>
            <Text fontSize="sm" lineHeight="1.7" mb={6}>
              Population shift to regional centers driving 12-15% annual growth in select markets. Brisbane and Gold Coast leading the charge.
            </Text>
            <Divider borderColor="whiteAlpha.400" />
            <Text fontSize="xs" mt={4} color="whiteAlpha.800">Data: Q4 2025</Text>
            <Box position="absolute" bottom="-20px" right="-20px" opacity={0.1}>
              <Icon as={FaChartLine} boxSize={32} />
            </Box>
          </Box>

          <Box bg="gradient.to-br" bgGradient="linear(to-br, green.500, green.700)" p={8} borderRadius="2xl" color="white" position="relative" overflow="hidden">
            <Badge colorScheme="green" bg="whiteAlpha.300" color="white" mb={4} px={3} py={1}>
              Yield Opportunity
            </Badge>
            <Heading size="lg" mb={4}>Rental Demand Surge</Heading>
            <Text fontSize="sm" lineHeight="1.7" mb={6}>
              Rental vacancy rates at historic lows (1.2% nationally). Premium on quality properties with sustainable features.
            </Text>
            <Divider borderColor="whiteAlpha.400" />
            <Text fontSize="xs" mt={4} color="whiteAlpha.800">Data: Q4 2025</Text>
            <Box position="absolute" bottom="-20px" right="-20px" opacity={0.1}>
              <Icon as={FaBrain} boxSize={32} />
            </Box>
          </Box>

          <Box bg="gradient.to-br" bgGradient="linear(to-br, purple.500, purple.700)" p={8} borderRadius="2xl" color="white" position="relative" overflow="hidden">
            <Badge colorScheme="purple" bg="whiteAlpha.300" color="white" mb={4} px={3} py={1}>
              Infrastructure
            </Badge>
            <Heading size="lg" mb={4}>Major Projects</Heading>
            <Text fontSize="sm" lineHeight="1.7" mb={6}>
              $120B in infrastructure spending announced. Transport corridors and new employment hubs creating opportunity zones.
            </Text>
            <Divider borderColor="whiteAlpha.400" />
            <Text fontSize="xs" mt={4} color="whiteAlpha.800">Data: Q4 2025</Text>
            <Box position="absolute" bottom="-20px" right="-20px" opacity={0.1}>
              <Icon as={FaMapMarkerAlt} boxSize={32} />
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg="blue.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaDatabase} boxSize={16} />
            <Heading size="xl" maxW="2xl">
              Get Clarity Before You Commit
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="whiteAlpha.900">
              Book a strategy session and we'll show you exactly what the data says about your investment options—so you can make decisions with confidence
            </Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyStrategyDataDriven;
